/**
 * Product page — supplier comparison and price history.
 *
 * Spec §43 and §44. The buyer's question is "who sells this, for how much, and
 * can I trust the number", and this page answers all three in one screen
 * without ever implying more precision than the data supports.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ProductGallery } from '@/components/data/gallery'
import {
  PriceStatisticsCards, PriceTrend, SupplierTable,
} from '@/components/data/prices'
import { PackageIcon, ScaleIcon } from '@/components/ui/icons'
import {
  Alert, Badge, cx, DataRow, EmptyState, Panel, SectionHeader, Skeleton,
} from '@/components/ui/primitives'
import { productApi } from '@/services/endpoints'
import { formatMoney, formatNumber, formatRelative, UNKNOWN } from '@/utils/format'

const HISTORY_RANGES = [
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 180, label: '6m' },
  { days: 365, label: '1y' },
]

export function ProductDetailPage() {
  const { slug = '' } = useParams()
  const [days, setDays] = useState(90)

  const product = useQuery({
    queryKey: ['product', slug],
    queryFn: () => productApi.detail(slug),
    enabled: Boolean(slug),
  })

  const history = useQuery({
    queryKey: ['product', slug, 'history', days],
    queryFn: () => productApi.priceHistory(slug, days),
    enabled: Boolean(slug),
  })

  if (product.isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Skeleton className="h-8 w-96" />
        <Skeleton className="mt-6 h-64" />
      </div>
    )
  }

  if (product.isError || !product.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState
          icon={<PackageIcon size={18} />}
          title="Product not found"
          description="This product does not exist, or is not published."
        />
      </div>
    )
  }

  const record = product.data
  const quoteOnly = record.suppliers.filter((offer) => !offer.is_comparable).length
  const comparable = record.suppliers.length - quoteOnly

  // Parsed attributes first — they are the ones we understood well enough to
  // filter on. Fall back to the verbatim specifications.
  const specChips: Array<[string, string]> = (
    record.attributes.length > 0
      ? record.attributes.map(
          (attribute) =>
            [
              attribute.name,
              `${attribute.value}${attribute.unit ? ` ${attribute.unit}` : ''}`,
            ] as [string, string],
        )
      : Object.entries(record.specifications).map(
          ([key, value]) => [key, String(value)] as [string, string],
        )
  ).slice(0, 4)

  return (
    <div>
      {/* -- Header --------------------------------------------------------- */}
      <header className="border-b border-[var(--border-subtle)] bg-[var(--surface-panel)]">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6">
          <nav className="mb-3 flex items-center gap-1.5 text-xs text-[var(--text-tertiary)]">
            <Link to="/products" className="hover:text-[var(--accent-text)]">Products</Link>
          </nav>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
                {record.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-[var(--text-secondary)]">
                {record.brand ? <Badge tone="neutral" size="xs">{record.brand.name}</Badge> : null}
                {record.unit ? <span>Sold per {record.unit}</span> : null}
                {record.sku ? <span>SKU {record.sku}</span> : null}
                {record.barcode ? <span className="tabular">GTIN {record.barcode}</span> : null}
                <span>Updated {formatRelative(record.price_updated_at ?? record.updated_at)}</span>
              </div>
              {record.short_description ? (
                <p className="mt-3 max-w-2xl text-sm text-[var(--text-secondary)]">
                  {record.short_description}
                </p>
              ) : null}

              {/* The specifications a buyer scans before they look at price —
                  grade, weight, capacity. Surfaced in the header rather than
                  buried below the fold, because they decide whether the price
                  below is even relevant. */}
              {specChips.length > 0 ? (
                <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                  {specChips.map(([key, value]) => (
                    <div key={key}>
                      <dt className="field-label">{key}</dt>
                      <dd className="mt-0.5 text-sm font-medium text-[var(--text-primary)]">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>

            {/* -- Headline price ------------------------------------------- */}
            <Panel className="w-full shrink-0 lg:w-72">
              {record.lowest_price ? (
                <>
                  <p className="field-label">Lowest published price</p>
                  <p className="mt-1 text-3xl font-semibold tabular text-[var(--text-primary)]">
                    {formatMoney(record.lowest_price)}
                  </p>
                  {record.unit ? (
                    <p className="text-xs text-[var(--text-tertiary)]">per {record.unit}</p>
                  ) : null}

                  <dl className="mt-4 space-y-1.5 border-t border-[var(--border-subtle)] pt-3 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-[var(--text-tertiary)]">Average</dt>
                      <dd className="tabular text-[var(--text-secondary)]">
                        {formatMoney(record.average_price)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-[var(--text-tertiary)]">Highest</dt>
                      <dd className="tabular text-[var(--text-secondary)]">
                        {formatMoney(record.highest_price)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-[var(--text-tertiary)]">Suppliers compared</dt>
                      <dd className="tabular text-[var(--text-secondary)]">
                        {comparable}
                        {quoteOnly > 0 ? (
                          <span className="text-[var(--text-tertiary)]">
                            {' '}+{quoteOnly} on request
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  </dl>

                  {/* The spread is the buyer's actual takeaway: what choosing
                      the right supplier is worth on this item. */}
                  {record.price_spread_percent ? (
                    <div className="mt-3 rounded-md bg-[var(--accent-subtle)] px-3 py-2">
                      <p className="text-xs text-[var(--accent-text)]">
                        <span className="font-semibold tabular">
                          {record.price_spread_percent}%
                        </span>{' '}
                        between the cheapest and dearest supplier.
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="field-label">Price</p>
                  <p className="mt-1 text-lg font-medium text-[var(--text-tertiary)]">
                    Not published
                  </p>
                  <p className="mt-2 text-xs text-[var(--text-secondary)]">
                    Every supplier for this product quotes on request. No average can be
                    computed — and a zero would be a lie.
                  </p>
                </>
              )}
            </Panel>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-7 sm:px-6">
        {/* -- Photos ------------------------------------------------------- */}
        {/* Hotlinked from the suppliers' own sites — the same places the
            prices came from — so they are labelled as theirs and every slide
            links back to where it was published. Products indexed before
            galleries existed still carry a single cover, hence the fallback. */}
        <ProductGallery
          images={
            (record.images?.length ?? 0) > 0
              ? record.images
              : record.image_url
                ? [{ url: record.image_url, source_url: null }]
                : []
          }
          alt={record.name}
        />

        {/* -- Trend -------------------------------------------------------- */}
        {history.data && history.data.series.length >= 2 ? (
          <Panel>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <SectionHeader
                title="Price trend"
                // One point per day on which a price was actually observed — not
                // a daily series with gaps filled in. Saying "periods" hid that
                // four points can span three months.
                description={`Average across all suppliers, on each of the ${history.data.series.length} days a price was observed.`}
              />
              <div className="inline-flex shrink-0 rounded-lg border border-[var(--border-subtle)] p-0.5">
                {HISTORY_RANGES.map((range) => (
                  <button
                    key={range.days}
                    type="button"
                    onClick={() => setDays(range.days)}
                    className={cx(
                      'rounded-md px-2.5 py-1 text-xs transition-colors',
                      days === range.days
                        ? 'bg-[var(--surface-active)] font-medium text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                    )}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <PriceTrend
                series={history.data.series}
                changePercent={history.data.change_percent}
              />
            </div>
          </Panel>
        ) : null}

        {/* -- Statistics --------------------------------------------------- */}
        {/* Suppressed at one supplier. A "Price summary" whose lowest, highest
            and average are the same number is not a summary — it is one price
            shown three times, and presenting it as a comparison overstates what
            this index knows. */}
        {record.statistics.length > 0 && comparable > 1 ? (
          <section>
            <SectionHeader
              title="Price summary"
              description="Grouped by currency and unit — ₦ per bag and ₦ per tonne are not comparable, so they are never averaged together."
              className="mb-3"
            />
            <PriceStatisticsCards statistics={record.statistics} />
          </section>
        ) : null}

        {/* -- Suppliers ---------------------------------------------------- */}
        <Panel padded={false}>
          <div className="p-5 pb-3">
            <SectionHeader
              title="Suppliers"
              description="Every published offer, with its source and the date it was observed."
            />
          </div>
          <div className="px-5 pb-5">
            <SupplierTable suppliers={record.suppliers} />
          </div>
        </Panel>

        {comparable === 1 ? (
          <Alert tone="info" title="One supplier indexed">
            This price is the only one on file for this product, so there is nothing
            to compare it against yet. A second supplier listing the same item is what
            turns this page into a comparison — until then, treat the figure as one
            seller's published price rather than a market rate.
          </Alert>
        ) : null}

        {quoteOnly > 0 && comparable === 0 ? (
          <Alert tone="info" title="No comparable prices">
            All {quoteOnly} supplier{quoteOnly === 1 ? '' : 's'} for this product quote on
            request. The listing is still useful — it tells you who sells it — but there is
            no number to compare.
          </Alert>
        ) : null}

        {/* -- Specifications ----------------------------------------------- */}
        {(record.attributes.length > 0 || Object.keys(record.specifications).length > 0) ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {record.attributes.length > 0 ? (
              <Panel>
                <SectionHeader title="Specifications" />
                <dl className="mt-2 divide-hairline">
                  {record.attributes.map((attribute) => (
                    <DataRow
                      key={attribute.name}
                      label={attribute.name}
                      value={`${attribute.value}${attribute.unit ? ` ${attribute.unit}` : ''}`}
                    />
                  ))}
                </dl>
              </Panel>
            ) : null}

            {Object.keys(record.specifications).length > 0 ? (
              <Panel>
                <SectionHeader
                  title="As published"
                  description="Kept verbatim from the source page."
                />
                <dl className="mt-2 divide-hairline">
                  {Object.entries(record.specifications).map(([key, value]) => (
                    <DataRow key={key} label={key} value={String(value)} />
                  ))}
                </dl>
              </Panel>
            ) : null}
          </div>
        ) : null}

        {record.description ? (
          <Panel>
            <SectionHeader title="Description" />
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[var(--text-secondary)]">
              {record.description}
            </p>
          </Panel>
        ) : null}

        <Panel className="bg-[var(--surface-sunken)]">
          <div className="flex gap-2.5">
            <ScaleIcon size={16} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
            <p className="text-2xs leading-relaxed text-[var(--text-secondary)]">
              Prices are observations from published pages, not quotations. They exclude
              delivery, taxes and negotiated terms, and can be out of date by the time you
              read them — every row shows when it was last checked. Confirm with the
              supplier before ordering.
            </p>
          </div>
        </Panel>
      </div>
    </div>
  )
}

export { formatNumber, UNKNOWN }
